import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { KeyValuePair, ProgramContainer } from '../../entities/formTemplateDataType';
import { GlobalKeyValueService } from '../../utilities/GlobalKeyValueService';

interface Props {
    programContainer: ProgramContainer;
    callback: (programContainer: ProgramContainer) => void;
    isEng: boolean;
}

const GlobalKeyValuePairsComponent: React.FC<Props> = ({ programContainer, callback, isEng }) => {
    const [keyValuePairs, setKeyValuePairs] = useState<KeyValuePair[]>([]);
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');
    const [editingPair, setEditingPair] = useState<KeyValuePair | null>(null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const toast = React.useRef<Toast>(null);

    useEffect(() => {
        loadKeyValuePairs();
    }, [programContainer.CategoryId]);

    const loadKeyValuePairs = () => {
        const pairs = GlobalKeyValueService.getKeyValuePairs(programContainer.CategoryId);
        setKeyValuePairs(pairs);
        programContainer.GlobalKeyValuePairs = pairs;
    };

    const addKeyValuePair = () => {
        if (!newKey.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: isEng ? 'Warning' : 'Avertissement',
                detail: isEng ? 'Key is required' : 'La clé est requise'
            });
            return;
        }

        const existingPair = keyValuePairs.find(p => p.key === newKey.trim());
        if (existingPair) {
            toast.current?.show({
                severity: 'warn',
                summary: isEng ? 'Warning' : 'Avertissement',
                detail: isEng ? 'Key already exists' : 'La clé existe déjà'
            });
            return;
        }

        const newPair: KeyValuePair = {
            key: newKey.trim(),
            value: newValue.trim(),
            appName: programContainer.CategoryId
        };

        GlobalKeyValueService.addKeyValuePair(programContainer.CategoryId, newPair);
        loadKeyValuePairs();
        callback(programContainer);

        setNewKey('');
        setNewValue('');

        toast.current?.show({
            severity: 'success',
            summary: isEng ? 'Success' : 'Succès',
            detail: isEng ? 'Key-value pair added' : 'Paire clé-valeur ajoutée'
        });
    };

    const editKeyValuePair = (pair: KeyValuePair) => {
        setEditingPair({ ...pair });
        setShowEditDialog(true);
    };

    const updateKeyValuePair = () => {
        if (!editingPair || !editingPair.key.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: isEng ? 'Warning' : 'Avertissement',
                detail: isEng ? 'Key is required' : 'La clé est requise'
            });
            return;
        }

        GlobalKeyValueService.addKeyValuePair(programContainer.CategoryId, editingPair);
        loadKeyValuePairs();
        callback(programContainer);
        setShowEditDialog(false);
        setEditingPair(null);

        toast.current?.show({
            severity: 'success',
            summary: isEng ? 'Success' : 'Succès',
            detail: isEng ? 'Key-value pair updated' : 'Paire clé-valeur mise à jour'
        });
    };

    const deleteKeyValuePair = (key: string) => {
        GlobalKeyValueService.removeKeyValuePair(programContainer.CategoryId, key);
        loadKeyValuePairs();
        callback(programContainer);

        toast.current?.show({
            severity: 'success',
            summary: isEng ? 'Success' : 'Succès',
            detail: isEng ? 'Key-value pair deleted' : 'Paire clé-valeur supprimée'
        });
    };


    const actionBodyTemplate = (rowData: KeyValuePair) => {
        return (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => editKeyValuePair(rowData)}
                    tooltip={isEng ? 'Edit' : 'Modifier'}
                />
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-text p-button-sm p-button-danger"
                    onClick={() => deleteKeyValuePair(rowData.key)}
                    tooltip={isEng ? 'Delete' : 'Supprimer'}
                />
            </div>
        );
    };

    return (
        <div style={{ padding: '0.5rem' }}>
            <Toast ref={toast} />
            
            <div style={{ marginBottom: '0.5rem' }}>
                <h3>{isEng ? 'Global Key-Value Pairs' : 'Paires Clé-Valeur Globales'}</h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'end' }}>
                <div>
                    <label htmlFor="newKey" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        {isEng ? 'Key' : 'Clé'} *
                    </label>
                    <InputText
                        id="newKey"
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value)}
                        placeholder={isEng ? 'e.g., orgId' : 'ex: orgId'}
                        style={{ width: '100%' }}
                    />
                </div>
                <div>
                    <label htmlFor="newValue" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                        {isEng ? 'Default Value' : 'Valeur par défaut'}
                    </label>
                    <InputText
                        id="newValue"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder={isEng ? 'Default value' : 'Valeur par défaut'}
                        style={{ width: '100%' }}
                    />
                </div>
                <Button
                    label={isEng ? 'Add' : 'Ajouter'}
                    icon="pi pi-plus"
                    onClick={addKeyValuePair}
                    className="p-button-sm"
                />
            </div>
            <br/>
            <DataTable
                value={keyValuePairs}
                emptyMessage={isEng ? 'No key-value pairs defined' : 'Aucune paire clé-valeur définie'}
                size="small"
            >
                <Column field="key" header={isEng ? 'Key' : 'Clé'} style={{ width: '40%' }} />
                <Column field="value" header={isEng ? 'Value' : 'Valeur'} style={{ width: '50%' }} />
                <Column body={actionBodyTemplate} header={isEng ? 'Actions' : 'Actions'} style={{ width: '10%' }} />
            </DataTable>

            <Dialog
                header={isEng ? 'Edit Key-Value Pair' : 'Modifier la Paire Clé-Valeur'}
                visible={showEditDialog}
                style={{ width: '500px' }}
                onHide={() => setShowEditDialog(false)}
                footer={
                    <div>
                        <Button
                            label={isEng ? 'Cancel' : 'Annuler'}
                            icon="pi pi-times"
                            onClick={() => setShowEditDialog(false)}
                            className="p-button-text"
                        />
                        <Button
                            label={isEng ? 'Update' : 'Mettre à jour'}
                            icon="pi pi-check"
                            onClick={updateKeyValuePair}
                        />
                    </div>
                }
            >
                {editingPair && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label htmlFor="editKey" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {isEng ? 'Key' : 'Clé'} *
                            </label>
                            <InputText
                                id="editKey"
                                value={editingPair.key}
                                onChange={(e) => setEditingPair({ ...editingPair, key: e.target.value })}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <label htmlFor="editValue" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {isEng ? 'Default Value' : 'Valeur par défaut'}
                            </label>
                            <InputText
                                id="editValue"
                                value={editingPair.value}
                                onChange={(e) => setEditingPair({ ...editingPair, value: e.target.value })}
                                placeholder={isEng ? 'Default value' : 'Valeur par défaut'}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}
            </Dialog>

        </div>
    );
};

export default GlobalKeyValuePairsComponent;
